import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./home.css";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import book from "../assets/book.png";
import candle from "../assets/candle.png";
import curtain from "../assets/curtain.png";
import desk from "../assets/desk.png";
import envelope from "../assets/envelope.png";
import flowers from "../assets/flowers.png";
import windowImg from "../assets/window.png";
import windowbg from "../assets/windowbg.png";
import quill from "../assets/quill.webp";

import topleft from "../assets/topleft.png";
import toplefteyes from "../assets/toplefteyes.png";
import topright from "../assets/topright.png";
import toprighteyes from "../assets/toprighteyes.png";
import bottomleft from "../assets/bottomleft.png";
import bottomlefteyes from "../assets/bottomlefteyes.png";
import bottomright from "../assets/bottomright.png";
import bottomrighteyes from "../assets/bottomrighteyes.png";
import emily from "../assets/emily.png";

import backgroundmusic from "../assets/backgroundmusic.mp3";
import quillwriting from "../assets/quillwriting.mp3";

import emilycollage from "../assets/emilycollage.png";
import shelf from "../assets/shelf.png";
import birds from "../assets/birds.png";
import butterfly from "../assets/butterfly.png";
import sun from "../assets/sun.png";
import cloud from "../assets/cloud.png";
import flowerscollage from "../assets/flowerscollage.png";
import sticker from "../assets/sticker.png";

import Footer from "../components/footer";
import FallingText from "../components/fallingtext";

import vine from "../assets/vine.png";
import paper from "../assets/paper.png";

gsap.registerPlugin(ScrollTrigger);

const left_text_full = "I dwell in\nPossibility";
const right_text_full = "A fairer House\nthan Prose";

const Home = ({ soundEnabled, setSoundEnabled }) => {
  const [introOpen, setIntroOpen] = useState(true);
  const [introClosing, setIntroClosing] = useState(false);
  const [showWriting, setShowWriting] = useState(false);
  const location = useLocation();

  const [leftText, setLeftText] = useState("");
  const [rightText, setRightText] = useState("");

  const background_music_ref = useRef(null);
  const quill_writing_ref = useRef(null);

  const hero_ref = useRef(null);
  const curtain_left_ref = useRef(null);
  const curtain_right_ref = useRef(null);
  const title_ref = useRef(null);
  const desk_ref = useRef(null);
  const book_wrap_ref = useRef(null);
  const candle_ref = useRef(null);
  const envelope_ref = useRef(null);
  const flowers_ref = useRef(null);

  const portraits_ref = useRef(null);
  const eyes_topleft_ref = useRef(null);
  const eyes_bottomleft_ref = useRef(null);
  const eyes_topright_ref = useRef(null);
  const eyes_bottomright_ref = useRef(null);

  const collage_ref = useRef(null);
  const collage_shelf_ref = useRef(null);
  const collage_birds_ref = useRef(null);
  const collage_butterfly_ref = useRef(null);
  const collage_sun_ref = useRef(null);
  const collage_cloud_ref = useRef(null);
  const collage_emily_ref = useRef(null);
  const collage_flowers_ref = useRef(null);
  const collage_note_ref = useRef(null);

  const timeline_ref = useRef(null);
  const timeline_vine_ref = useRef(null);
  const timeline_card_1_ref = useRef(null);
  const timeline_card_2_ref = useRef(null);
  const timeline_card_3_ref = useRef(null);
  const timeline_card_4_ref = useRef(null);
  const timeline_card_5_ref = useRef(null);
  const timeline_card_6_ref = useRef(null);

  useEffect(() => {
    const bg = background_music_ref.current;

    if (!bg) return;

    bg.volume = 0.19;
    bg.loop = true;
    bg.playbackRate = 0.7;

    if (!introOpen && soundEnabled) {
      const playPromise = bg.play();

      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }

      return;
    }

    bg.pause();
  }, [introOpen, soundEnabled]);

  useEffect(() => {
    if (soundEnabled || !quill_writing_ref.current) return;

    quill_writing_ref.current.pause();
    quill_writing_ref.current.currentTime = 0;
  }, [soundEnabled]);

  useEffect(() => {
    let scrollTimer;

    scrollTimer = window.setTimeout(() => {
      if (!location.hash) {
        if (location.pathname === "/") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        return;
      }

      const target = document.getElementById(location.hash.replace("#", ""));

      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, introOpen ? 1100 : 80);

    return () => {
      window.clearTimeout(scrollTimer);
    };
  }, [introOpen, location.hash, location.pathname]);

  useEffect(() => {
    if (!introOpen) {
      let left_interval;
      let right_interval;
      let quill_stop_timer;

      const start_timer = setTimeout(() => {
        setShowWriting(true);

        if (soundEnabled && quill_writing_ref.current) {
          quill_writing_ref.current.currentTime = 0;
          quill_writing_ref.current.volume = 0.45;
          quill_writing_ref.current.playbackRate = 0.6;
          quill_writing_ref.current.play();
        }

        quill_stop_timer = setTimeout(() => {
          if (quill_writing_ref.current) {
            quill_writing_ref.current.pause();
            quill_writing_ref.current.currentTime = 0;
          }
        }, 6200);

        let left_index = 0;
        let right_index = 0;

        left_interval = setInterval(() => {
          if (left_index < left_text_full.length) {
            setLeftText(left_text_full.slice(0, left_index + 1));
            left_index++;
          } else {
            clearInterval(left_interval);

            right_interval = setInterval(() => {
              if (right_index < right_text_full.length) {
                setRightText(right_text_full.slice(0, right_index + 1));
                right_index++;
              } else {
                clearInterval(right_interval);
              }
            }, 115);
          }
        }, 125);
      }, 200);

      return () => {
        clearTimeout(start_timer);
        clearTimeout(quill_stop_timer);
        clearInterval(left_interval);
        clearInterval(right_interval);
      };
    }
  }, [introOpen, soundEnabled]);

  useLayoutEffect(() => {
    if (!hero_ref.current) return;

    const ctx = gsap.context(() => {
  gsap.to(curtain_left_ref.current, {
    y: 80,
    ease: "none",
    scrollTrigger: {
      trigger: hero_ref.current,
      start: "top top",
      end: "+=450",
      scrub: true,
    },
  });

      gsap.to(curtain_right_ref.current, {
        y: 120,
        ease: "none",
        scrollTrigger: {
          trigger: hero_ref.current,
          start: "top top",
          end: "+=450",
          scrub: true,
        },
      });

      gsap.to(title_ref.current, {
        y: 100,
        ease: "none",
        scrollTrigger: {
          trigger: hero_ref.current,
          start: "top top",
          end: "+=450",
          scrub: true,
        },
      });

      gsap.to(desk_ref.current, {
        y: 120,
        ease: "none",
        scrollTrigger: {
          trigger: hero_ref.current,
          start: "top top",
          end: "+=450",
          scrub: true,
        },
      });

      gsap.to(book_wrap_ref.current, {
        y: 150,
        ease: "none",
        scrollTrigger: {
          trigger: hero_ref.current,
          start: "top top",
          end: "+=450",
          scrub: true,
        },
      });

      gsap.to(candle_ref.current, {
        y: 160,
        ease: "none",
        scrollTrigger: {
          trigger: hero_ref.current,
          start: "top top",
          end: "+=450",
          scrub: true,
        },
      });

      gsap.to(envelope_ref.current, {
        y: 160,
        ease: "none",
        scrollTrigger: {
          trigger: hero_ref.current,
          start: "top top",
          end: "+=450",
          scrub: true,
        },
      });

      gsap.to(flowers_ref.current, {
        y: 320,
        ease: "none",
        scrollTrigger: {
          trigger: hero_ref.current,
          start: "top top",
          end: "+=900",
          scrub: true,
        },
      });

      gsap.to(
        [
          eyes_topleft_ref.current,
          eyes_bottomleft_ref.current,
          eyes_topright_ref.current,
          eyes_bottomright_ref.current,
        ],
        {
          scale: 5,
          opacity: 1,
          ease: "none",
          scrollTrigger: {
            trigger: portraits_ref.current,
            start: "bottom 50%",
            end: "bottom top",
            scrub: true,
          },
        }
      );

      gsap.to(".portraits_title", {
        y: 170,
        ease: "none",
        scrollTrigger: {
          trigger: portraits_ref.current,
          start: "top 60%",
          end: "bottom 30%",
          scrub: true,
        },
      });

      [
        [".portrait_top_left", 150, -26, -3],
        [".portrait_bottom_left", 120, -36, -4],
        [".portrait_center", 140, 0, 0],
        [".portrait_top_right", 170, 24, 3],
        [".portrait_bottom_right", 120, 36, 4],
      ].forEach(([selector, y, x, rotate]) => {
        gsap.to(selector, {
          y,
          x,
          rotate,
          ease: "none",
          scrollTrigger: {
            trigger: portraits_ref.current,
            start: "top 60%",
            end: "bottom 30%",
            scrub: true,
          },
        });
      });

      let collage_ambient_started = false;

      const start_collage_ambient = () => {
        if (collage_ambient_started) return;
        collage_ambient_started = true;

        gsap.to(collage_sun_ref.current, {
          rotate: 360,
          duration: 18,
          ease: "none",
          repeat: -1,
          transformOrigin: "50% 50%",
        });

        gsap.to(collage_birds_ref.current, {
          keyframes: [
            { x: 20, y: -18, rotate: 4, duration: 2.2, ease: "sine.inOut" },
            { x: 36, y: 8, rotate: -3, duration: 2.4, ease: "sine.inOut" },
            { x: 8, y: 14, rotate: 2, duration: 2.1, ease: "sine.inOut" },
            { x: 0, y: 0, rotate: 0, duration: 2, ease: "sine.inOut" },
          ],
          repeat: -1,
        });

        gsap.to(collage_butterfly_ref.current, {
          keyframes: [
            { x: -28, y: -24, rotate: -10, duration: 1.8, ease: "sine.inOut" },
            { x: 22, y: -42, rotate: 8, duration: 1.6, ease: "sine.inOut" },
            { x: 42, y: -8, rotate: 12, duration: 1.7, ease: "sine.inOut" },
            { x: -12, y: 20, rotate: -6, duration: 1.9, ease: "sine.inOut" },
            { x: 0, y: 0, rotate: 0, duration: 1.6, ease: "sine.inOut" },
          ],
          repeat: -1,
        });

        gsap.to(collage_cloud_ref.current, {
          x: 18,
          y: -6,
          rotate: 2,
          duration: 4.2,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });

        gsap.to(collage_flowers_ref.current, {
          xPercent: -50,
          x: 10,
          rotate: 3,
          duration: 3.1,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          transformOrigin: "50% 100%",
        });
      };

      const collage_tl = gsap.timeline({
        scrollTrigger: {
          trigger: collage_ref.current,
          start: "top 75%",
          toggleActions: "play none none none",
        },
        onComplete: start_collage_ambient,
      });

      collage_tl
        .fromTo(
          collage_shelf_ref.current,
          {
            opacity: 0,
            x: -80,
            y: 20,
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
          }
        )
        .fromTo(
          collage_birds_ref.current,
          {
            opacity: 0,
            x: -40,
            y: -30,
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
          },
          "-=0.35"
        )
        .fromTo(
          collage_butterfly_ref.current,
          {
            opacity: 0,
            y: -50,
            rotate: -8,
          },
          {
            opacity: 1,
            y: 0,
            rotate: 0,
            duration: 0.7,
            ease: "power2.out",
          },
          "-=0.25"
        )
        .fromTo(
          collage_sun_ref.current,
          {
            opacity: 0,
            x: 80,
            y: -30,
            scale: 0.85,
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.35"
        )
        .fromTo(
          collage_cloud_ref.current,
          {
            opacity: 0,
            x: 60,
          },
          {
            opacity: 1,
            x: 0,
            duration: 0.7,
            ease: "power2.out",
          },
          "-=0.2"
        )
        .fromTo(
          collage_emily_ref.current,
          {
            opacity: 0,
            xPercent: -50,
            y: 60,
            scale: 0.92,
          },
          {
            opacity: 1,
            xPercent: -50,
            y: 0,
            scale: 1,
            duration: 0.9,
            ease: "power2.out",
          },
          "-=0.15"
        )
        .fromTo(
          collage_flowers_ref.current,
          {
            opacity: 0,
            xPercent: -50,
            y: 80,
            scale: 0.95,
          },
          {
            opacity: 1,
            xPercent: -50,
            y: 0,
            scale: 1,
            duration: 0.9,
            ease: "power2.out",
          },
          "-=0.25"
        )
        .fromTo(
          collage_note_ref.current,
          {
            opacity: 0,
            xPercent: -50,
            y: 40,
            scale: 0.92,
          },
          {
            opacity: 1,
            xPercent: -50,
            y: 0,
            scale: 1,
            duration: 0.8,
            ease: "power2.out",
          },
          "+=0.15"
        );

      gsap.fromTo(
      ".timeline_title",
      {
        opacity: 0,
        y: 30,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: timeline_ref.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );

    const timeline_tl = gsap.timeline({
      scrollTrigger: {
        trigger: timeline_ref.current,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });

    timeline_tl
      .fromTo(
        timeline_vine_ref.current,
        {
          opacity: 0,
          scale: 0.92,
          y: -20,
        },
        {
          opacity: 1,
          scale: 1.05,
          y: 0,
          duration: 0.9,
          ease: "power2.out",
        }
      )
      .fromTo(
        ".timeline_vine_2",
        {
          opacity: 0,
          scale: 0.9,
          y: 20,
        },
        {
          opacity: 1,
          scale: 1.1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        "-=0.45"
      )
      .fromTo(
        ".timeline_vine_3",
        {
          opacity: 0,
          scale: 0.9,
          y: -10,
        },
        {
          opacity: 1,
          scale: 1.05,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        "-=0.45"
      )
      .fromTo(
        timeline_card_1_ref.current,
        {
          opacity: 0,
          y: -40,
          rotate: -7,
        },
        {
          opacity: 1,
          y: 0,
          rotate: -3,
          duration: 0.6,
          ease: "power2.out",
        }
      )
      .fromTo(
        timeline_card_2_ref.current,
        {
          opacity: 0,
          y: 40,
          rotate: 5,
        },
        {
          opacity: 1,
          y: 0,
          rotate: -2,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.25"
      )
      .fromTo(
        timeline_card_3_ref.current,
        {
          opacity: 0,
          y: 30,
          xPercent: -50,
          rotate: -5,
        },
        {
          opacity: 1,
          y: 0,
          xPercent: -50,
          rotate: 2,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.25"
      )
      .fromTo(
        timeline_card_4_ref.current,
        {
          opacity: 0,
          y: -30,
          xPercent: -50,
          rotate: 4,
        },
        {
          opacity: 1,
          y: 0,
          xPercent: -50,
          rotate: -1,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.2"
      )
      .fromTo(
        timeline_card_5_ref.current,
        {
          opacity: 0,
          y: 40,
          rotate: -4,
        },
        {
          opacity: 1,
          y: 0,
          rotate: 3,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.25"
      )
      .fromTo(
        timeline_card_6_ref.current,
        {
          opacity: 0,
          y: 35,
          rotate: 6,
        },
        {
          opacity: 1,
          y: 0,
          rotate: -3,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.25"
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <main>
      <audio ref={background_music_ref} src={backgroundmusic} />
      <audio ref={quill_writing_ref} src={quillwriting} />

      {introOpen && (
        <div
          className={`window_intro ${introClosing ? "closing" : ""}`}
          onClick={() => {
            setIntroClosing(true);
            setTimeout(() => {
              setIntroOpen(false);
            }, 1000);
          }}
        >
          <div className="window_dark"></div>

          <div className="window_intro_stage">
            <img src={windowbg} className="window_bg" alt="" />
            <div className="window_intro_copy">
              <p>Lift the latch on possibility</p>
            </div>
            <img src={windowImg} className="window_image" alt="" />
            <img src={flowers} className="window_intro_flowers" alt="" />
            <div className="window_glow"></div>
          </div>

          <div
            className="intro_controls"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`sound_button ${soundEnabled ? "active" : ""}`}
              type="button"
              onClick={() => setSoundEnabled((current) => !current)}
            >
              {soundEnabled ? "Sound On" : "Sound Off"}
            </button>
          </div>
        </div>
      )}

      <section className="hero" id="home" ref={hero_ref}>
        <div className="scene">
          <img
            src={curtain}
            className="curtain_left"
            alt=""
            ref={curtain_left_ref}
          />
          <img
            src={curtain}
            className="curtain_right"
            alt=""
            ref={curtain_right_ref}
          />

          <div className="title" ref={title_ref}>
            <h1>Emily Dickinson</h1>
            <p>A story about a poet</p>
          </div>

          <img src={desk} className="desk" alt="" ref={desk_ref} />

          <div className="book_wrap" ref={book_wrap_ref}>
            <img src={book} className="book" alt="" />

            <div className="book_writing">
              <div className="page_text left_page_text">{leftText}</div>
              <div className="page_text right_page_text">{rightText}</div>
            </div>

            <img
              src={quill}
              className={`quill ${showWriting ? "animate" : ""}`}
              alt=""
            />
          </div>

          <img src={candle} className="candle" alt="" ref={candle_ref} />
          <img src={envelope} className="envelope" alt="" ref={envelope_ref} />
          <img src={flowers} className="flowers" alt="" ref={flowers_ref} />
        </div>
      </section>

      <section className="portraits" id="portraits" ref={portraits_ref}>
  <h2 className="portraits_title">The Dickinsons</h2>

  <div className="portraits_stage">

    <div className="portrait_card portrait_top_left">
      <img src={topleft} className="portrait_base" alt="" />
      <img
        src={toplefteyes}
        className="portrait_eyes eyes_topleft eyes_shift delay_one"
        alt=""
        ref={eyes_topleft_ref}
      />

      <div className="portrait_overlay">
        <h3>Lavinia Dickinson</h3>
        <p className="overlay_title">The keeper of the house.</p>
        <p>She stood beside Emily in life and rescued her words after death.</p>
      </div>
    </div>


    <div className="portrait_card portrait_bottom_left">
      <img src={bottomleft} className="portrait_base" alt="" />
      <img
        src={bottomlefteyes}
        className="portrait_eyes eyes_bottomleft eyes_shift delay_two"
        alt=""
        ref={eyes_bottomleft_ref}
      />

      <div className="portrait_overlay">
        <h3>Edward Dickinson</h3>
        <p className="overlay_title">The law of the home.</p>
        <p>He offered protection, but his discipline helped build the walls around her.</p>
      </div>
    </div>


    <div className="portrait_card portrait_center">
      <img src={emily} className="portrait_base" alt="" />
    </div>


    <div className="portrait_card portrait_top_right">
      <img src={topright} className="portrait_base" alt="" />
      <img
        src={toprighteyes}
        className="portrait_eyes eyes_topright eyes_shift delay_three"
        alt=""
        ref={eyes_topright_ref}
      />

      <div className="portrait_overlay">
        <h3>Austin Dickinson</h3>
        <p className="overlay_title">The brother next door.</p>
        <p>Close in distance, farther in spirit, he belonged to the life Emily kept apart from.</p>
      </div>
    </div>


    <div className="portrait_card portrait_bottom_right">
      <img src={bottomright} className="portrait_base" alt="" />
      <img
        src={bottomrighteyes}
        className="portrait_eyes eyes_bottomright eyes_shift delay_four"
        alt=""
        ref={eyes_bottomright_ref}
      />

      <div className="portrait_overlay">
        <h3>Susan Gilbert Dickinson</h3>
        <p className="overlay_title">The beloved reader.</p>
        <p>Part muse, part wound, she inspired Emily and left traces of longing and distance.</p>
      </div>
    </div>

  </div>
</section>
    <section className="collage" id="collage" ref={collage_ref}>
      <div className="collage_stage">
        <img src={shelf} className="collage_shelf" alt="" ref={collage_shelf_ref} />
        <img src={birds} className="collage_birds" alt="" ref={collage_birds_ref} />
        <img src={butterfly} className="collage_butterfly" alt="" ref={collage_butterfly_ref} />
        <img src={sun} className="collage_sun" alt="" ref={collage_sun_ref} />
        <img src={cloud} className="collage_cloud" alt="" ref={collage_cloud_ref} />
        <img src={emilycollage} className="collage_emily" alt="" ref={collage_emily_ref} />
        <img src={flowerscollage} className="collage_flowers" alt="" ref={collage_flowers_ref} />

        <div className="collage_note_wrap" ref={collage_note_ref}>
          <img src={sticker} className="collage_sticker" alt="" />
          <p className="collage_note">
            The collage represents Dickinson’s inner world, where nature symbolizes emotion, transformation, and the imagination she nurtured within confinement.
          </p>
        </div>
      </div>
      <div className="collage_words">
      <FallingText
        text="Nature transformation preservation confinement imagination solitude longing possibility silence memory wildness wonder devotion stillness transformation imagination longing preservation possibility wonder"
        highlightWords={["Nature", "transformation", "preservation", "imagination", "possibility", "wonder"]}
        highlightClass="highlighted"
        trigger="auto"
        backgroundColor="transparent"
        wireframes={false}
        gravity={0.12}
        fontSize="2rem"
        mouseConstraintStiffness={0.8}
      />
      </div>
    </section>

    <section className="timeline" id="timeline" ref={timeline_ref}>
      <h2 className="timeline_title">A Life in Fragments</h2>

      <div className="timeline_stage">
        <img src={vine} className="timeline_vine timeline_vine_1" alt="" ref={timeline_vine_ref} />
        <img src={vine} className="timeline_vine timeline_vine_2" alt="" />
        <img src={vine} className="timeline_vine timeline_vine_3" alt="" />

        <div className="timeline_card timeline_card_1" ref={timeline_card_1_ref}>
          <img src={paper} className="timeline_paper" alt="" />
          <div className="timeline_text">
            <h3>1830</h3>
            <p>Born in Amherst, Massachusetts.</p>
          </div>
        </div>

        <div className="timeline_card timeline_card_2" ref={timeline_card_2_ref}>
          <img src={paper} className="timeline_paper" alt="" />
          <div className="timeline_text">
            <h3>1847</h3>
            <p>Studies at Mount Holyoke Female Seminary.</p>
          </div>
        </div>

        <div className="timeline_card timeline_card_3" ref={timeline_card_3_ref}>
          <img src={paper} className="timeline_paper" alt="" />
          <div className="timeline_text">
            <h3>1855</h3>
            <p>Begins shaping her poetic voice.</p>
          </div>
        </div>

        <div className="timeline_card timeline_card_4" ref={timeline_card_4_ref}>
          <img src={paper} className="timeline_paper" alt="" />
          <div className="timeline_text">
            <h3>1862</h3>
            <p>Writes to Thomas Higginson.</p>
          </div>
        </div>

        <div className="timeline_card timeline_card_5" ref={timeline_card_5_ref}>
          <img src={paper} className="timeline_paper" alt="" />
          <div className="timeline_text">
            <h3>1865</h3>
            <p>Writes many of her most powerful poems.</p>
          </div>
        </div>

        <div className="timeline_card timeline_card_6" ref={timeline_card_6_ref}>
          <img src={paper} className="timeline_paper" alt="" />
          <div className="timeline_text">
            <h3>1886</h3>
            <p>Dies in Amherst, leaving hundreds of poems behind.</p>
          </div>
        </div>
      </div>
    </section>
    <Footer />
    </main>
  );
};

export default Home;
